/**
 * Badge sorgente appuntamento (Task #595).
 *
 * Componente riusabile che renderizza un badge coerente con la tassonomia
 * unificata in `lib/appointment-source-taxonomy`. Usato in Inbox, Calendar
 * tab, Planning, CRM appointment list.
 */

import {
  FileText, Target, Store, Bot, PhoneIncoming, PhoneCall,
  UserCog, Sparkles, Plug, HelpCircle,
  type LucideIcon,
} from 'lucide-react';
import { Badge } from '@easyflow/ui-frontend/components/ui/badge';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '@easyflow/ui-frontend/components/ui/tooltip';
import { getSourceDescriptor, type AppointmentSourceKind } from '@/lib/appointment-source-taxonomy';

const ICON_MAP: Record<string, LucideIcon> = {
  FileText, Target, Store, Bot, PhoneIncoming, PhoneCall,
  UserCog, Sparkles, Plug,
};

interface Props {
  kind?: AppointmentSourceKind | string | null;
  /** Mostra solo l'icona (senza testo) — utile in spazi stretti. */
  iconOnly?: boolean;
  /** Classi extra. */
  className?: string;
  /** Dimensione testuale (default: 10px). */
  size?: 'xs' | 'sm';
}

export default function AppointmentSourceBadge({
  kind,
  iconOnly = false,
  className = '',
  size = 'xs',
}: Props) {
  const desc = getSourceDescriptor(kind);
  const Icon = desc ? (ICON_MAP[desc.icon] || HelpCircle) : HelpCircle;
  const label = desc?.label || 'Sconosciuta';
  const description = desc?.description || 'Sorgente non riconosciuta';
  const bg = desc?.bgClass || 'bg-slate-100 dark:bg-slate-800/50';
  const text = desc?.textClass || 'text-slate-700 dark:text-slate-300';
  const border = desc?.borderClass || 'border-slate-300 dark:border-slate-600';
  const textSize = size === 'sm' ? 'text-xs' : 'text-[10px]';

  const badge = (
    <Badge
      className={`gap-1 border ${bg} ${text} ${border} ${textSize} ${className}`}
      title={iconOnly ? label : undefined}
    >
      <Icon className="w-3 h-3 flex-shrink-0" />
      {!iconOnly && <span className="truncate">{label}</span>}
    </Badge>
  );

  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span className="inline-flex">{badge}</span>
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs max-w-xs">
          <div className="font-semibold">{label}</div>
          <div className="text-muted-foreground mt-0.5">{description}</div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
